import React, { useCallback } from 'react';
import { Box, Button, Typography, Alert, LinearProgress, Stack, CircularProgress } from '@mui/material';
import { CloudUpload, SaveAlt } from '@mui/icons-material';
import { UploadProgress } from '../types';

interface UploadSectionProps {
  isUploading: boolean;
  progress: UploadProgress | null;
  error: string | null;
  selectedDuration: number;
  maxDuration: number;
  onUpload: () => void;
  onReset: () => void;
  onClearError: () => void;
  formatTime: (seconds: number) => string;
  disabled?: boolean;
  mediaType?: 'video' | 'audio';
  recoveryFile?: { file: File; name: string } | null;
}

const isIndexedDbError = (message: string | null): boolean => {
  if (!message) return false;
  const lowered = message.toLowerCase();
  return lowered.includes('indexed database') || lowered.includes('indexeddb');
};

export const UploadSection: React.FC<UploadSectionProps> = ({
  isUploading,
  progress,
  error,
  selectedDuration,
  maxDuration,
  onUpload,
  onReset,
  onClearError,
  formatTime,
  disabled = false,
  mediaType = 'video',
  recoveryFile = null,
}) => {
  const label = mediaType === 'audio' ? 'Audio' : 'Video';
  const isWithinLimit = selectedDuration <= maxDuration;
  const overLimitAmount = selectedDuration - maxDuration;

  const handleSaveToDevice = useCallback(() => {
    if (!recoveryFile) return;
    const objectUrl = URL.createObjectURL(recoveryFile.file);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = recoveryFile.name;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    // Revoke after the click handler has had a chance to dispatch
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  }, [recoveryFile]);

  if (isUploading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: { xs: 6, sm: 8 },
          px: 3,
          textAlign: 'center',
        }}
      >
        <CloudUpload sx={{ fontSize: 56, color: 'primary.main', mb: 2 }} />
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
          Uploading Your {label}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4 }}>
          Please don&apos;t close this page while the upload is in progress.
        </Typography>

        {progress ? (
          <Box sx={{ width: '100%', maxWidth: 480 }}>
            <LinearProgress
              variant="determinate"
              value={progress.progress}
              sx={{ height: 10, borderRadius: 5, mb: 1.5 }}
            />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {progress.progress}% complete
            </Typography>
          </Box>
        ) : (
          <CircularProgress size={40} />
        )}
      </Box>
    );
  }

  const hasIdbError = isIndexedDbError(error);
  const errorMessage = hasIdbError
    ? `Upload failed: your browser dropped its storage connection. Save your ${label.toLowerCase()} to your device, then try again.`
    : error
      ? `Upload failed: ${error}`
      : null;

  const canRecover = Boolean(error && recoveryFile);

  return (
    <>
      {/* Upload Error */}
      {errorMessage && (
        <Box sx={{ mb: 3 }}>
          <Alert severity="error" onClose={onClearError}>
            <Typography variant="body2">{errorMessage}</Typography>
            {canRecover && !hasIdbError && (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                Save your {label.toLowerCase()} to your device to keep a copy before retrying.
              </Typography>
            )}
          </Alert>
        </Box>
      )}

      {/* Action Buttons */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
        <Button variant="outlined" color="inherit" onClick={onReset} disabled={disabled} size="large">
          Record Again
        </Button>
        {canRecover && (
          <Button
            variant="outlined"
            color="primary"
            onClick={handleSaveToDevice}
            startIcon={<SaveAlt />}
            size="large"
            sx={{ minWidth: 200 }}
          >
            Save {label} to Device
          </Button>
        )}
        <Button
          variant="contained"
          color="success"
          onClick={onUpload}
          disabled={!isWithinLimit || disabled}
          startIcon={<CloudUpload />}
          size="large"
          sx={{ minWidth: 200 }}
        >
          {isWithinLimit
            ? `Upload ${formatTime(selectedDuration)} ${label}`
            : `${label} exceeds limit by ${formatTime(overLimitAmount)}`}
        </Button>
      </Stack>
    </>
  );
};

export default UploadSection;
